import { cn } from '@/shared/utilities';
import { Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { Blurhash } from 'react-blurhash';
import { useTranslation } from 'react-i18next';
import type { MediaAttachment } from '../../models/attachment';
import { useAttachmentUrl } from '../../queries/use-attachment-url';

interface ReplyMediaPreviewProps {
  attachment: MediaAttachment;
}

export const ReplyMediaPreview = memo<ReplyMediaPreviewProps>(({ attachment }) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const { blobUrl, isError: hasError } = useAttachmentUrl(attachment.url ?? undefined);

  // Determine which URL to use
  const imageUrl = blobUrl || attachment.url;
  const shouldShowImage = imageUrl && !imageError;

  // Reset state when URL changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [imageUrl]);

  // Handle image load
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Handle image error
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  const showError = hasError || imageError;

  return (
    <div className='mex:flex mex:items-center mex:gap-2'>
      <div className='mex:relative mex:h-8 mex:w-8 mex:flex-shrink-0 mex:overflow-hidden mex:rounded mex:bg-gray-200'>
        {/* Blurhash background - always show until image loads */}
        <div
          className={cn(
            'mex:absolute mex:inset-0 mex:z-0 mex:flex mex:h-full mex:w-full mex:items-center mex:justify-center mex:transition-opacity mex:duration-300',
            {
              'mex:opacity-0': imageLoaded,
              'mex:opacity-100': !imageLoaded
            }
          )}
          role='img'
          aria-label={imageLoaded ? undefined : t('messages.reply.loadingImage')}
        >
          {attachment.blurHash ? (
            <Blurhash
              hash={attachment.blurHash}
              width='100%'
              height='100%'
              resolutionX={32}
              resolutionY={32}
              punch={1}
              className='mex:h-full mex:w-full'
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <div className='mex:h-full mex:w-full mex:bg-gray-100' />
          )}
        </div>

        {/* Actual image */}
        {shouldShowImage && (
          <img
            src={imageUrl}
            alt={attachment.filename || 'Media'}
            loading='lazy'
            decoding='async'
            onLoad={handleImageLoad}
            onError={handleImageError}
            className={cn(
              'mex:relative mex:z-10 mex:h-full mex:w-full mex:transition-opacity mex:duration-300 mex:ease-in-out',
              {
                'mex:opacity-100': imageLoaded,
                'mex:opacity-0': !imageLoaded
              }
            )}
            style={{ objectFit: 'cover' }}
          />
        )}

        {/* Placeholder icon - show when no image or error */}
        {showError && (
          <div className='mex:absolute mex:inset-0 mex:flex mex:h-full mex:w-full mex:items-center mex:justify-center mex:bg-gray-200'>
            <svg
              className='mex:h-4 mex:w-4 mex:text-gray-400'
              fill='currentColor'
              viewBox='0 0 20 20'
              aria-hidden='true'
            >
              <path
                fillRule='evenodd'
                d='M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z'
                clipRule='evenodd'
              />
            </svg>
          </div>
        )}
      </div>
      <Typography variant='body2' noWrap sx={{ color: 'text.primary' }}>
        {t('messages.reply.mediaTypes.image')}
      </Typography>
    </div>
  );
});
